import { useCallback, useEffect, useMemo, useState } from 'react';
import type { GlobalAlert } from '../api/globalAlerts.types';
import {
  ALERTS_DISMISSED_STORAGE_KEY,
  fetchDismissedAlertKeys,
  storeDismissedAlertKey
} from '../utils/globalAlertsUtils';

export const useGlobalAlertsState = (alerts: Array<GlobalAlert>): [Array<GlobalAlert>, (id: string) => void] => {
  const [dismissedAlerts, setDismissedAlerts] = useState<Array<string>>([]);

  const dismissAlert = useCallback((id: string) => {
    storeDismissedAlertKey(id);
    setDismissedAlerts((prev) => [...prev, id]);
  }, []);

  // Cross-tab sync via storage events
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === ALERTS_DISMISSED_STORAGE_KEY) {
        setDismissedAlerts(fetchDismissedAlertKeys());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Seed dismissed list from localStorage once alerts load
  useEffect(() => {
    if (alerts.length > 0) {
      setDismissedAlerts(fetchDismissedAlertKeys());
    }
  }, [alerts]);

  const filteredAlerts = useMemo(
    () => alerts.filter((alert) => !dismissedAlerts.includes(alert.id)),
    [alerts, dismissedAlerts]
  );

  return [filteredAlerts, dismissAlert];
};
